import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  first,
  from,
  map,
  Observable,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { ConfigService } from '../config-service/config.service';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import Swal from 'sweetalert2';

export type User = {
  'contact-info': {
    email: string;
    name: string;
    phone: string;
    userid: string;
  };
  'company-info': {
    name: string;
    address: {
      city: string;
      'postal-code': string;
      state: string;
      street: string;
      country: string;
    };
  };
};

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  public config: any = null;
  private accessToken = '';

  ACCESS_TYPE_RAIL_PERFORMANCE = 'Rail Performance';
  ACCESS_TYPE_FORECAST_AND_DEMAND = 'Forecast and Demand';

  private userSource$ = new BehaviorSubject<any>(null);
  user$ = this.userSource$.asObservable();

  private userCache$: Observable<any> | null = null;
  private isAuthenticated = false; // Cached session status
  private isUserValid = true;

  constructor(
    private configService: ConfigService,
    private httpClient: HttpClient,
    public auth: AuthService
  ) {
    const strConfig = localStorage.getItem('config-service');
    if (strConfig !== null) {
      this.config = JSON.parse(strConfig);
    }

    // Subscribe to `isAuthenticated$` to keep `isAuthenticated` up-to-date
    this.auth.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });
  }

  setUser(user: any) {
    this.userSource$.next(user);
  }

  getSessionToken(): string | null {
    return this.accessToken || null;
  }

  getSessionTokenFromOauth(): Observable<string | null> {
    if (this.accessToken) {
      return new BehaviorSubject(this.accessToken).asObservable();
    }

    // Otherwise, get the token from Auth0 and cache it
    return from(this.auth.getAccessTokenSilently()).pipe(
      map((token: string) => {
        this.accessToken = token;
        return token;
      }),
      catchError(() => new BehaviorSubject(null).asObservable())
    );
  }

  checkAccessForRailPerformance(email: string): Observable<any> {
    return this.getAccessDetails(email, this.ACCESS_TYPE_RAIL_PERFORMANCE);
  }

  checkAccessForForecastAndDemand(email: string): Observable<any> {
    return this.getAccessDetails(email, this.ACCESS_TYPE_FORECAST_AND_DEMAND);
  }

  getAccessDetails(email: string, applicationName: string): Observable<any> {
    const token = this.getSessionToken();
    if (token === null) this.signout();

    const params = {
      email: email,
      applicationName: applicationName,
    };

    const headers = new HttpHeaders({
      accept: 'application/json, text/plain, */*',
      Authorization: `Bearer ${token}`,
    });

    return this.httpClient.get<any>(this.configService.railPerformanceAPIURL + '/User/Application/Access', { headers, params });
  }

  getUser(): Observable<any> {
    if (this.userCache$) {
      return this.userCache$;
    }

    if (!this.config) {
      return throwError(() => new Error('Configuration is not available'));
    }
    this.userCache$ = this.auth.user$.pipe(
      switchMap((userProfile) => {
        if (userProfile && userProfile.email) {
          return this.checkEmail(userProfile.email).pipe(
            switchMap((validationResponse: any) => {
              this.isUserValid = validationResponse.validated;
              if (validationResponse.validated) {
                return from(this.getSessionTokenFromOauth()).pipe(
                  switchMap((accessToken: string | null) => {
                    if (!accessToken) {
                      return new Observable((observer) => observer.error());
                    }
                    const email: string = userProfile.email || '';
                    this.setSession(email, 60);
                    localStorage.setItem('userEmail', email);
                    const headers = new HttpHeaders({
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${accessToken}`,
                    });
                    const params = new HttpParams().set('email', email);
                    return this.httpClient.post(
                      this.config.clientProfileURL,
                      null,
                      {
                        headers: headers,
                        params: params,
                      }
                    );
                  })
                );
              } else {
                this.setUser(null);
                this.userCache$ = null;
                localStorage.setItem('userEmail', '');
                Swal.fire({
                  position: 'center',
                  text: 'PLEASE REQUEST ACCESS',
                  icon: 'info',
                  timer: 3000,
                  showConfirmButton: false,
                }).then(() => {
                  setTimeout(() => {
                    this.signout();
                    this.clearSession();
                  }, 1000);
                });
                this.isAuthenticated = false;
                return new Observable();
              }
            })
          );
        } else {
          this.setUser(null);
          this.userCache$ = null;
          return new Observable();
        }
      }),
      catchError((error) => {
        this.userCache$ = null;
        return new Observable((observer) => observer.error());
      }),
      shareReplay(1)
    );

    return this.userCache$;
  }

  getUserEmail(): Observable<string> {
    const sessionValid = this.isSessionValid();

    if (!sessionValid) {
      return throwError(() => new Error('Session is not valid'));
    }

    return this.getUser().pipe(
      map((response: any) => {
        const userData = response[0]?.json_build_object;
        if (
          userData &&
          userData['contact-info'] &&
          userData['contact-info'].email
        ) {
          return userData['contact-info'].email;
        }
      })
    );
  }

  isTrinUser(email: string): Observable<boolean> {
    if (this.config === null)
      return new Observable<boolean>((observer) => observer.next(false));

    return this.getSessionTokenFromOauth().pipe(
      switchMap((token) => {
        if (!token) {
          return new Observable<boolean>((observer) => observer.next(false));
        }

        const params = new HttpParams().set('email', email);
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        return this.httpClient
          .post<any>(
            this.config.checkUserDomainURL,
            {},
            {
              headers: headers,
              params: params,
            }
          )
          .pipe(map((response: any) => response.isTrinUser ?? false));
      })
    );
  }

  signupUser(userData: any): Observable<any> {
    const headers = {
      'Content-Type': 'application/json',
    };
    return this.httpClient.post(
      this.configService.signupUnprovisionedUsers,
      userData,
      {
        headers,
      }
    );
  }

  loginWithRedirect(): void {
    this.auth.loginWithRedirect();
  }

  logout(): void {
    this.auth.logout({ logoutParams: { returnTo: window.location.origin } });
  }

  isSessionValid(): boolean {
    const sessionExpiry = localStorage.getItem('sessionExpiry');
    if (sessionExpiry) {
      const expiryDate = new Date(sessionExpiry);
      if (expiryDate <= new Date()) {
        this.clearSession(); // Expired session
        return false;
      }
    }
    const userEmail = localStorage.getItem('userEmail');
    return this.isAuthenticated || !!userEmail;
  }

  clearSession(): void {
    localStorage.removeItem('userEmail');
    localStorage.removeItem('sessionExpiry');
    this.isAuthenticated = false;
    this.userSource$.next(null);
  }

  setSession(userEmail: string, durationInMinutes: number): void {
    const expiryDate = new Date();
    expiryDate.setMinutes(expiryDate.getMinutes() + durationInMinutes);

    localStorage.setItem('userEmail', userEmail);
    localStorage.setItem('sessionExpiry', expiryDate.toISOString());
  }

  isValidUser(): boolean {
    return this.isUserValid;
  }

  signout(): void {
    this.clearSession();
    this.logout();
  }

  checkEmail(email: string): Observable<any> {
    return this.getSessionTokenFromOauth().pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('Token is null'));
        }

        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });
        const params = new HttpParams().set('email', email);

        return this.httpClient.post(
          this.configService.validateAccountURL,
          null,
          { headers, params }
        );
      }),
      first(),
      catchError((error) => {
        return throwError(() => new Error('Failed to check email: ' + error));
      })
    );
  }

  getCognitoUserPool() {
    const poolData = {
      UserPoolId: this.config.cognitoUserPoolId,
      ClientId: this.config.cognitoAppClientId,
    };
    return new CognitoUserPool(poolData);
  }
}
